import React, { useEffect, useState } from "react";
import axios from "axios";
import { MyDomain } from "../../App";

export default function BarberSubsHistory() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const barberRaw = localStorage.getItem("barber");
      const barber = barberRaw ? JSON.parse(barberRaw) : null;

      if (!barber?.phoneNumber) {
        setError("Session expired. Please log in again.");
        setLoading(false);
        return;
      }

      const response = await axios.post(`${MyDomain}Subscription/GetAllSubs`, {
        phoneNumber: barber.phoneNumber,
      });

      // Sort by id descending (newest first)
      const sorted = (response.data || []).sort((a, b) => b.id - a.id);
      setSubs(sorted);
    } catch (err) {
      setError("Failed to load history. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // "DD-MM-YYYY" → "Apr 6, 2026"
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const [day, month, year] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getPlanName = (thePlan) => {
    switch (thePlan) {
      case 1:
        return "Free";
      case 2:
        return "Basic";
      case 3:
        return "Standard";
      case 4:
        return "Premium";
      case 5:
        return "Pro";
      default:
        return "Pro";
    }
  };

  const getStatus = (status) => {
    switch (status) {
      case 1:
        return {
          label: "Active",
          color: "#c8a96e",
          bg: "rgba(200,169,110,0.1)",
          border: "rgba(200,169,110,0.3)",
        };
      case 2:
        return {
          label: "Cancelled",
          color: "#8a5a4a",
          bg: "rgba(138,90,74,0.1)",
          border: "rgba(138,90,74,0.3)",
        };
      default:
        return {
          label: "Unknown",
          color: "#5a5048",
          bg: "rgba(90,80,72,0.1)",
          border: "rgba(90,80,72,0.3)",
        };
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.centered}>
        <span style={styles.loadingIcon}>✦</span>
        <p style={styles.loadingText}>Loading history…</p>
        <style>{fonts}</style>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={styles.centered}>
        <p style={styles.errorText}>⚠ {error}</p>
        <button style={styles.retryBtn} onClick={fetchHistory}>
          Retry
        </button>
        <style>{fonts}</style>
      </div>
    );
  }

  // ── Empty ────────────────────────────────────────────────────────────────
  if (subs.length === 0) {
    return (
      <div style={styles.centered}>
        <p style={styles.emptyIcon}>◌</p>
        <p style={styles.emptyTitle}>No history yet</p>
        <p style={styles.emptyText}>
          Your subscription history will appear here.
        </p>
        <style>{fonts}</style>
      </div>
    );
  }

  // ── List ─────────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <style>{fonts}</style>

      <div style={styles.main}>
        <div style={styles.topText}>
          <p style={styles.eyebrow}>SUBSCRIPTION HISTORY</p>
          <h1 style={styles.headline}>
            Your <em style={styles.em}>Records</em>
          </h1>
          <p style={styles.subline}>
            {subs.length} subscription{subs.length !== 1 ? "s" : ""} found
          </p>
        </div>

        <div style={styles.list}>
          {subs.map((sub, i) => {
            const status = getStatus(sub.status);
            const isLatest = i === 0;
            return (
              <div key={sub.id} style={styles.card}>
                {/* Card header */}
                {/* <div style={styles.cardHeader}>
                  <div style={styles.cardHeaderLeft}>
                    <span style={styles.cardIndex}>#{subs.length - i}</span>
                    <span style={styles.cardPlan}>
                      {getPlanName(sub.thePlan)} Plan
                    </span>
                    {isLatest && <span style={styles.latestBadge}>Latest</span>}
                  </div>
                  <span
                    style={{
                      ...styles.statusBadge,
                      color: status.color,
                      background: status.bg,
                      border: `1px solid ${status.border}`,
                    }}
                  >
                    {status.label}
                  </span>
                </div> */}

                <div style={styles.cardDivider} />

                {/* Card body */}
                <div style={styles.cardBody}>
                  <div style={styles.cardRow}>
                    <span style={styles.cardLabel}>START DATE</span>
                    <span style={styles.cardValue}>
                      {formatDate(sub.dateStart)}
                    </span>
                  </div>
                  <div style={styles.cardRow}>
                    <span style={styles.cardLabel}>END DATE</span>
                    <span style={styles.cardValue}>
                      {formatDate(sub.dateEnd)}
                    </span>
                  </div>
                  <div style={styles.cardRow}>
                    <span style={styles.cardLabel}>DATE PAID</span>
                    <span style={styles.cardValue}>
                      {formatDate(sub.datePay)}
                    </span>
                  </div>
                  <div style={styles.cardRow}>
                    <span style={styles.cardLabel}>AMOUNT</span>
                    <span style={{ ...styles.cardValue, color: "#c8a96e" }}>
                      ${sub.price}/mo
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const fonts = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=DM+Mono:wght@300;400&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #0f0d0a; }
`;

const styles = {
  page: {
    minHeight: "100vh",
    background: "#0f0d0a",
    color: "#e8ddd0",
    fontFamily: "'DM Mono', monospace",
  },
  main: {
    maxWidth: "700px",
    margin: "0 auto",
    padding: "60px 20px 80px",
  },
  topText: {
    textAlign: "center",
    marginBottom: "48px",
  },
  eyebrow: {
    fontSize: "11px",
    letterSpacing: "0.3em",
    color: "#c8a96e",
    marginBottom: "12px",
  },
  headline: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "48px",
    fontWeight: 400,
    lineHeight: 1.1,
    marginBottom: "12px",
    color: "#f0e8dc",
  },
  em: { fontStyle: "italic", color: "#c8a96e" },
  subline: {
    fontSize: "12px",
    color: "#5a5048",
    letterSpacing: "0.05em",
  },

  // List
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  // Card
  card: {
    background: "#17140f",
    border: "1px solid #2a2520",
    borderRadius: "14px",
    padding: "20px 24px",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "14px",
  },
  cardHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  cardIndex: {
    fontSize: "10px",
    color: "#3a3028",
    letterSpacing: "0.1em",
  },
  cardPlan: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "18px",
    fontWeight: 600,
    color: "#e8ddd0",
  },
  latestBadge: {
    fontSize: "9px",
    letterSpacing: "0.15em",
    color: "#c8a96e",
    background: "rgba(200,169,110,0.08)",
    border: "1px solid rgba(200,169,110,0.25)",
    borderRadius: "20px",
    padding: "2px 10px",
  },
  statusBadge: {
    fontSize: "10px",
    letterSpacing: "0.1em",
    borderRadius: "20px",
    padding: "3px 12px",
    flexShrink: 0,
  },
  cardDivider: {
    height: "1px",
    background: "#1e1a14",
    marginBottom: "14px",
  },
  cardBody: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px 24px",
  },
  cardRow: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  cardLabel: {
    fontSize: "9px",
    letterSpacing: "0.2em",
    color: "#4a4038",
  },
  cardValue: {
    fontSize: "13px",
    color: "#c8b89a",
  },

  // States
  centered: {
    minHeight: "60vh",
    background: "#0f0d0a",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    fontFamily: "'DM Mono', monospace",
  },
  loadingIcon: { color: "#c8a96e", fontSize: "24px" },
  loadingText: { fontSize: "12px", letterSpacing: "0.2em", color: "#5a5048" },
  errorText: { fontSize: "13px", color: "#d07060" },
  retryBtn: {
    background: "transparent",
    border: "1px solid #3a3028",
    color: "#8a7a6a",
    padding: "8px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    fontSize: "12px",
    letterSpacing: "0.08em",
  },
  emptyIcon: { fontSize: "28px", color: "#3a3028" },
  emptyTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "28px",
    color: "#4a4038",
  },
  emptyText: { fontSize: "12px", color: "#3a3028", letterSpacing: "0.05em" },
};
